import React, { useState, useEffect } from 'react';
import {
  BsX,
  BsImage,
  BsCheck,
  BsPencil,
  BsChevronLeft,
  BsChevronRight,
} from 'react-icons/bs';
import {
  showError,
  showLoading,
  showSuccess,
  dismissToast,
} from '../../components/Ui/ToastNotification';
import { useSelector } from 'react-redux';
import {
  createDashboardImage,
  deleteDashboardImage,
  getDashboardImages,
  updateDashboardImage,
  uploadFile,
} from '../../services/ImageUpload';

const ImageUpload = () => {
  const { userData } = useSelector((state) => state.auth);
  const userEmail = userData?.email;
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 10,
  });

  const HEADER_CHAR_LIMIT = 45;
  const TITLE_CHAR_LIMIT = 25;

  useEffect(() => {
    fetchDashboardImages(pagination.currentPage);
  }, []);

  const fetchDashboardImages = async (page) => {
    try {
      const response = await getDashboardImages(page, 10);

      setImages(
        response.dashboardImages.map((img) => ({
          ...img,
          id: img._id,
          previewUrl: img.imageUrl,
          uploading: false,
          error: false,
        })),
      );

      setPagination(response.pagination);
    } catch (error) {
      showError('Failed to load dashboard images');
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSelectedImageId(null);
    setIsEditing(false);
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    if (value.length <= TITLE_CHAR_LIMIT) {
      setTitle(value);
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= HEADER_CHAR_LIMIT) {
      setDescription(value);
    }
  };

  const handleSelectImage = (image) => {
    setSelectedImageId(image.id);
    setTitle(image.title || '');
    setDescription(image.description || '');
    setIsEditing(true);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleFileSelection = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!title) {
      showError('Please enter a title before uploading');
      return;
    }

    const MAX_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      showError('File size too large. Maximum size is 5MB.');
      return;
    }

    try {
      const tempImageId = `temp-${Date.now()}`;
      const previewUrl = URL.createObjectURL(file);

      const tempImage = {
        title,
        previewUrl,
        description,
        uploading: true,
        id: tempImageId,
      };

      setImages((prev) => [...prev, tempImage]);

      try {
        const uploadResponse = await uploadFile(file, userEmail);
        const imageUrl = uploadResponse.url;

        if (isEditing && selectedImageId) {
          const response = await updateDashboardImage(selectedImageId, {
            title,
            description,
            imageUrl,
          });

          setImages((prev) =>
            prev.map((img) =>
              img.id === selectedImageId
                ? {
                    ...img,
                    title,
                    description,
                    imageUrl: response.dashboardImage?.imageUrl || imageUrl,
                    previewUrl: response.dashboardImage?.imageUrl || previewUrl,
                    updatedAt: new Date().toISOString(),
                    uploading: false,
                  }
                : img,
            ),
          );
        } else {
          const response = await createDashboardImage({
            title,
            description,
            imageUrl,
          });

          const dashboardImage =
            response.dashboardImage || response.DashboardImage;

          setImages((prev) =>
            prev.map((img) =>
              img.id === tempImageId
                ? {
                    ...img,
                    id: dashboardImage._id,
                    title: dashboardImage.title,
                    imageUrl: dashboardImage.imageUrl,
                    description: dashboardImage.description,
                    previewUrl: dashboardImage.imageUrl,
                    createdAt: dashboardImage.createdAt,
                    updatedAt: dashboardImage.updatedAt,
                    uploading: false,
                  }
                : img,
            ),
          );
          await fetchDashboardImages(pagination.currentPage);
        }

        showSuccess(
          isEditing
            ? 'Image updated successfully!'
            : 'Image uploaded successfully!',
        );
        resetForm();
      } catch (error) {
        setImages((prev) => prev.filter((img) => img.id !== tempImageId));
        showError(
          isEditing ? 'Failed to update image' : 'Failed to upload image',
        );
        console.error(isEditing ? 'Update error:' : 'Upload error:', error);
      }
    } catch (error) {
      showError(
        error.message ||
          (isEditing ? 'Failed to update image' : 'Failed to upload image'),
      );
      console.error(isEditing ? 'Update error:' : 'Upload error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWithoutFile = async () => {
    if (!selectedImageId || !isEditing) return;

    const loadingToastId = showLoading('Updating image details...');
    setLoading(true);

    try {
      await updateDashboardImage(selectedImageId, {
        title,
        description,
      });

      setImages((prev) =>
        prev.map((img) =>
          img.id === selectedImageId
            ? {
                ...img,
                title,
                description,
              }
            : img,
        ),
      );

      showSuccess('Image details updated successfully!');
      resetForm();
    } catch (error) {
      showError('Failed to update image details');
      console.error('Update error:', error);
    } finally {
      dismissToast(loadingToastId);
      setLoading(false);
    }
  };

  const handleDeleteImage = async (imageId, index) => {
    const loadingToastId = showLoading('Deleting image...');

    try {
      if (!imageId.startsWith('temp-')) {
        await deleteDashboardImage(imageId);
      }

      setImages((prev) => prev.filter((_, i) => i !== index));

      if (selectedImageId === imageId) {
        resetForm();
      }

      await fetchDashboardImages(pagination.currentPage);

      showSuccess('Image deleted successfully!');
    } catch (error) {
      showError('Failed to delete image');
      console.error('Delete error:', error);
    } finally {
      dismissToast(loadingToastId);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchDashboardImages(newPage);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const { currentPage, totalPages } = pagination;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-1 text-sm transition border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          1
        </button>,
      );
      if (startPage > 2) {
        pages.push(
          <span key="dots1" className="px-2">
            ...
          </span>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 text-sm rounded-lg transition ${
            currentPage === i
              ? 'bg-black text-white'
              : 'border border-gray-300 hover:bg-gray-50'
          }`}
        >
          {i}
        </button>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="dots2" className="px-2">
            ...
          </span>,
        );
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1 text-sm transition border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {totalPages}
        </button>,
      );
    }

    return pages;
  };

  return (
    <div className="min-h-screen p-4 font-nohemi bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="p-6 mb-8 bg-white rounded-xl">
          <h2 className="pb-3 mb-6 text-base font-semibold border-b">
            {isEditing ? 'Edit Image' : 'Upload Image'}
          </h2>

          <div className="flex flex-col gap-6">
            <div>
              <label
                htmlFor="image-title"
                className="block mb-1 text-sm font-bold text-gray-700"
              >
                Title (max {TITLE_CHAR_LIMIT} characters)
              </label>
              <input
                id="image-title"
                type="text"
                value={title}
                onChange={handleTitleChange}
                maxLength={TITLE_CHAR_LIMIT}
                className="w-full p-2 text-sm transition border border-gray-300 rounded-lg focus:ring-0 focus:outline-none"
                placeholder="Enter image title..."
                required
              />
            </div>

            <div>
              <label
                htmlFor="image-description"
                className="block mb-1 text-sm font-bold text-gray-700"
              >
                Description (max {HEADER_CHAR_LIMIT} characters)
              </label>
              <textarea
                id="image-description"
                value={description}
                onChange={handleDescriptionChange}
                maxLength={HEADER_CHAR_LIMIT}
                className="w-full p-3 text-sm transition border border-gray-300 rounded-lg focus:ring-0 focus:outline-none"
                placeholder="Describe this image..."
                rows={3}
              />
            </div>

            <div className="w-full md:w-1/3">
              <label className="block mb-1 text-sm font-bold text-gray-700">
                {isEditing ? 'Replace Image (Optional)' : 'Image Upload'}
              </label>
              <label
                htmlFor="image-upload"
                className="flex flex-col items-center justify-center w-full h-48 transition duration-300 border-2 border-gray-300 border-dashed cursor-pointer rounded-xl bg-gray-50 hover:bg-gray-100"
                aria-label="Image Upload"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <BsImage className="w-10 h-10 mb-3 text-gray-400" />
                  <div className="text-center">
                    <p className="text-sm text-black">
                      <span className="font-medium">
                        {isEditing
                          ? 'Click to replace image'
                          : 'Click to upload image'}
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      PNG or JPEG (Max 5MB)
                    </p>
                  </div>
                </div>
                <input
                  id="image-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileSelection}
                  accept="image/png, image/jpeg"
                  disabled={loading}
                />
              </label>
            </div>

            {isEditing && (
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={resetForm}
                  className="px-4 py-2 text-xs transition duration-300 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateWithoutFile}
                  className="px-4 py-2 text-xs font-medium text-white transition duration-300 bg-black rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800"
                  disabled={loading || !selectedImageId}
                >
                  Update Details
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 bg-white rounded-xl">
          <h2 className="flex items-center justify-between pb-3 mb-6 text-base font-semibold border-b">
            <span>Image Gallery</span>
            <span className="text-sm text-gray-500">
              {pagination.total} {pagination.total === 1 ? 'image' : 'images'}
            </span>
          </h2>

          {images.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              <BsImage className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No images uploaded yet</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {images.map((image, index) => (
                  <div
                    key={image.id}
                    className={`relative bg-gray-50 rounded-xl overflow-hidden border 
                      ${selectedImageId === image.id ? 'border-black' : 'border-gray-200'} 
                      transition-all`}
                  >
                    <div
                      className="relative bg-gray-200 cursor-pointer aspect-video"
                      onClick={() => handleSelectImage(image)}
                    >
                      <img
                        src={image.previewUrl || image.url}
                        alt={image.description || `Image ${index + 1}`}
                        className="object-cover w-full h-full"
                      />

                      {image.uploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                          <div className="w-8 h-8 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                        </div>
                      )}

                      {image.error && (
                        <div className="absolute px-2 py-1 text-xs text-white bg-red-500 rounded-md top-2 right-2">
                          Error
                        </div>
                      )}

                      {!image.uploading && !image.error && (
                        <div className="absolute flex items-center px-2 py-1 text-sm text-white bg-green-500 rounded-md top-2 right-2">
                          <BsCheck className="mr-1" />
                          Uploaded
                        </div>
                      )}

                      {selectedImageId === image.id && (
                        <div className="absolute flex items-center px-2 py-1 text-sm text-white bg-blue-500 rounded-md top-2 left-2">
                          <BsPencil className="mr-1" />
                          Editing
                        </div>
                      )}

                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(image.id, index);
                        }}
                        className="absolute p-1 text-white transition-all bg-black rounded-full bottom-2 right-2 bg-opacity-70 hover:bg-opacity-90"
                      >
                        <BsX size={20} />
                      </button>
                    </div>

                    <button
                      className="w-full p-4 text-left cursor-pointer"
                      onClick={() => handleSelectImage(image)}
                    >
                      <p className="py-2 text-sm font-medium text-black">
                        {image.title}
                      </p>
                      <p className="text-sm text-gray-500">
                        {image.description || 'No description provided'}
                      </p>
                    </button>
                  </div>
                ))}
              </div>

              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-8">
                  <p className="text-sm text-gray-600">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        handlePageChange(pagination.currentPage - 1)
                      }
                      disabled={pagination.currentPage === 1}
                      className="p-2 transition border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <BsChevronLeft size={16} />
                    </button>

                    <div className="flex items-center gap-2">
                      {renderPageNumbers()}
                    </div>

                    <button
                      onClick={() =>
                        handlePageChange(pagination.currentPage + 1)
                      }
                      disabled={
                        pagination.currentPage === pagination.totalPages
                      }
                      className="p-2 transition border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <BsChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
